import { useNavigate } from "react-router-dom";
import { useState, useEffect, useRef } from "react";
import Cookies from 'js-cookie';
import { gsap } from 'gsap';

const translations = {
  en: {
    candidate: "Candidate",
    reports: "Reports",
    reportList: "Report List",
    filterByDate: "Sort by Date Generated:",
    newestFirst: "Newest First",
    oldestFirst: "Oldest First",
    name: "Name",
    user: "User",
    dateGenerated: "Date Generated",
    status: "Status",
    actions: "Actions",
    loading: "Loading reports...",
    noReports: "No Reports Yet",
    completed: "Completed",
    view: "View",
    delete: "Delete",
    noData: "No data"
  },
  nl: {
    candidate: "Kandidaat",
    reports: "Rapporten",
    reportList: "Rapportenlijst",
    filterByDate: "Sorteer op gegenereerde datum:",
    newestFirst: "Nieuwste eerst",
    oldestFirst: "Oudste eerst",
    name: "Naam",
    user: "Gebruiker",
    dateGenerated: "Datum gegenereerd",
    status: "Status",
    actions: "Acties",
    loading: "Rapporten laden...",
    noReports: "Nog geen rapporten",
    completed: "Voltooid",
    view: "Bekijken",
    delete: "Verwijderen",
    noData: "Geen gegevens"
  }
};

function Reports({reports, setReports, loading, setLoading, error, setError, setReportBackLink}) {
  const navigate = useNavigate();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [filterByDate, setFilterByDate] = useState("newestFirst");
  const dropdownRef = useRef(null);
  
  const [lang, setLang] = useState(
    Cookies.get("isDutch") === "true" ? "nl" : "en"
  );
  const t = translations[lang];

  useEffect(() => {
    const interval = setInterval(() => {
      const cookieLang = Cookies.get("isDutch") === "true" ? "nl" : "en";
      if (cookieLang !== lang) setLang(cookieLang);
    }, 1000);
    return () => clearInterval(interval);
  }, [lang]);
  
  const sortedReports = [...reports].sort((a, b) => {
    const dateA = new Date(a.datetimeInserted);
    const dateB = new Date(b.datetimeInserted);
    return filterByDate === "newestFirst" ? dateB - dateA : dateA - dateB;
  });

  const handleDelete = async (reportId) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/report/new/${reportId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${Cookies.get('token')}`,
          'Content-Type': 'application/json',
          'accept': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to delete report');
      }

      setReports(reports.filter(report => report.id !== reportId));
    } catch (err) {
      setError(err.message);
    }
  };

  const setGSAPProperties = (element, props) => {
    if (props.force3D) {
      element.style.transform = 'translate3d(0, 0, 0)';
    }
    if (props.willChange) {
      element.style.willChange = props.willChange;
    }
  };

  const resetGSAPProperties = (element) => {
    element.style.willChange = 'auto';
  };

  // Direct fadeUp animation
  const initFadeUp = () => {
    const fadeUpElements = document.querySelectorAll(".fadeUp");

    if (fadeUpElements.length === 0) {
      setTimeout(initFadeUp, 100);
      return;
    }

    // Set CSS properties directly for better performance
    fadeUpElements.forEach(element => {
      setGSAPProperties(element, { force3D: true, willChange: "transform, opacity" });
    });

    const animationDelay = 0; // Base delay in milliseconds

    gsap.set(fadeUpElements, { 
      opacity: 0, 
      y: 20
    });
    
    gsap.to(fadeUpElements, {
      opacity: 1,
      y: 0,
      duration: 0.6,
      delay: 0.2 + (animationDelay / 1000), // Add navbar delay to base delay
      stagger: 0.1,
      ease: "power2.out",
      onComplete: () => {
        // Reset CSS properties after animation
        fadeUpElements.forEach(element => {
          resetGSAPProperties(element);
        });
      }
    });
  };

  useEffect(() => {
    if(reports){
      initFadeUp();
    }
  }, [reports]);

  return (
    <div className="m-0 text-left">
      <h1 className="text-5xl font-bold mb-4 text-primary">{t.reports}</h1>
      <main className="flex flex-col gap-4">
        <div className="flex justify-between gap-2 items-center mt-8">
          <h2 className="text-2xl font-bold">{t.reportList}</h2>
          <div className="flex items-center gap-4">
            <span>{t.filterByDate}</span>
            <div ref={dropdownRef} className="relative">
              <div
                className="border p-2 rounded-md cursor-pointer flex items-center gap-2 hover:bg-gray-50"
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
              >
                <span>
                  {filterByDate === "newestFirst"
                    ? t.newestFirst
                    : t.oldestFirst}
                </span>
                <svg
                  className={`w-4 h-4 transition-transform ${
                    isDropdownOpen ? "rotate-180" : ""
                  }`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </div>

              {isDropdownOpen && (
                <div className="absolute top-full left-0 mt-1 w-full bg-white border rounded-md shadow-lg z-10">
                  <div
                    className="p-2 hover:bg-gray-50 cursor-pointer rounded-md"
                    onClick={() => {
                      setFilterByDate("newestFirst");
                      setIsDropdownOpen(false);
                    }}
                  >
                    {t.newestFirst}
                  </div>
                  <div
                    className="p-2 hover:bg-gray-50 cursor-pointer rounded-md"
                    onClick={() => {
                      setFilterByDate("oldestFirst");
                      setIsDropdownOpen(false);
                    }}
                  >
                    {t.oldestFirst}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
        <table className="w-full border-collapse border border-gray-300">
          <thead>
            <tr>
              <th className="border border-gray-300 p-2">{t.name}</th>
              <th className="border border-gray-300 p-2">{t.user}</th>
              <th className="border border-gray-300 p-2">{t.dateGenerated}</th>
              <th className="border border-gray-300 p-2">{t.status}</th>
              <th className="border border-gray-300 p-2">{t.actions}</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan="5" className="border border-gray-300 p-2">{t.loading}</td>
              </tr>
            )}
            {error && (
              <tr>
                <td colSpan="5" className="border border-gray-300 p-2">{t.noReports}</td>
              </tr>
            )}
            {sortedReports.map((report) => (
              <tr key={report.id} className="fadeUp">
                <td className="border border-gray-300 p-2">{report.linkedInData.firstName ? `${report.linkedInData.firstName} ${report.linkedInData.lastName}` : t.candidate}</td>
                <td className="border border-gray-300 p-2">{report.userEmail || t.noData}</td>
                <td className="border border-gray-300 p-2">{new Date(report.datetimeInserted).toLocaleDateString()}</td>
                <td className="border border-gray-300 p-2">{t.completed}</td>
                <td className="border border-gray-300 p-2">
                  <div className="flex gap-2">
                    <button className="border bg-default text-white px-4 py-2 rounded-md"
                    onClick={() => 
                    {
                      navigate(`/reports/${report.id}`)
                      setReportBackLink("/reports")
                    }
                    }
                    >{t.view}</button>
                    <button className="border px-4 py-2 rounded-md"
                    onClick={() => handleDelete(report.id)}
                    >{t.delete}</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </main>
    </div>
  );
}

export default Reports; 